import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { IconClose } from '../utils/icons';

const PRIORITY_OPTIONS = [
  { value: 'alta',  label: 'Alta'  },
  { value: 'media', label: 'Media' },
  { value: 'baja',  label: 'Baja'  },
];

const STATUS_TICKET_OPTIONS = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'listo',     label: 'Listo'     },
];

const PRIO_TAG_PREFIX = '__prio:';
const STATUS_TAG_PREFIX = '__status:';

export function derivePriorityFromTags(tags = []) {
  const found = (tags || []).find(t => typeof t === 'string' && t.startsWith(PRIO_TAG_PREFIX));
  if (found) {
    const p = found.split(':')[1]?.toLowerCase();
    if (['alta','media','baja'].includes(p)) return p;
  }
  const simple = (tags || []).find(t => ['alta','media','baja'].includes(String(t).toLowerCase()));
  return simple || 'media';
}

export function buildTagsFromPriority(priority = 'media') {
  return [`${PRIO_TAG_PREFIX}${priority}`];
}

export function deriveTicketStatusFromTags(tags = []) {
  const found = (tags || []).find(t => typeof t === 'string' && t.startsWith(STATUS_TAG_PREFIX));
  if (found) {
    const s = found.split(':')[1]?.toLowerCase();
    if (['pendiente','listo'].includes(s)) return s;
  }
  return 'pendiente';
}

export function buildTagsFromStatus(status = 'pendiente') {
  return [`${STATUS_TAG_PREFIX}${status}`];
}

export const PrioritySelect = ({ value='media', onChange, rounded='rounded-xl' }) => (
  <select
    value={value}
    onChange={(e)=>onChange?.(e.target.value)}
    className={`${rounded} w-full border-slate-200 focus:ring-2 focus:ring-slate-900/20`}
  >
    {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export function TicketModal({ open, onClose, onSubmit, defaultValue }) {
  const { token } = useAuth();
  const [form, setForm] = useState({
    id: null, title:'', content:'', pinned:false, priority:'media', status:'pendiente'
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(()=>{
    if (defaultValue) {
      setForm({
        id: defaultValue.id,
        title: defaultValue.title || '',
        content: defaultValue.content || '',
        pinned: !!defaultValue.pinned,
        priority: defaultValue.priority || derivePriorityFromTags(defaultValue.tags),
        status: defaultValue.status || deriveTicketStatusFromTags(defaultValue.tags),
      });
    } else {
      setForm({ id:null, title:'', content:'', pinned:false, priority:'media', status:'pendiente' });
    }
  }, [defaultValue]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      const payload = {
        company: defaultValue?.company || 'PERSONAL',
        title: form.title,
        content: form.content,
        tags: [...buildTagsFromPriority(form.priority), ...buildTagsFromStatus(form.status)].join(','),
        pinned: form.pinned
      };
      let res;
      if (form.id) {
        res = await fetch(`/api/owner/notes/${form.id}`, {
          method:'PUT',
          headers:{ 'Content-Type':'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`/api/owner/notes`, {
          method:'POST',
          headers:{ 'Content-Type':'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
      }
      const data = await res.json().catch(()=> ({}));
      if (!res.ok) throw new Error(data.mensaje || 'Error al guardar');
      onSubmit(); onClose();
    } catch (err) {
      setMsg(err.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-900 to-slate-700 px-5 py-3">
          <h3 className="text-white font-semibold">{form.id ? 'Editar ticket' : 'Nuevo ticket'}</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white"><IconClose/></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <input
                placeholder="Título"
                value={form.title}
                onChange={(e)=>setForm(f=>({...f, title: e.target.value}))}
                className="w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Prioridad</label>
              <PrioritySelect value={form.priority} onChange={(v)=>setForm(f=>({...f, priority:v}))} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2" />
            <div>
              <label className="text-xs font-medium text-slate-600">Estatus</label>
              <select
                value={form.status}
                onChange={(e)=>setForm(f=>({...f, status: e.target.value}))}
                className="rounded-xl w-full border-slate-200 focus:ring-2 focus:ring-slate-900/20"
              >
                {STATUS_TICKET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <textarea
              rows={8}
              placeholder="Detalle del ticket. Usa checklist con '- [ ] Tarea' o '- [x] Hecho'."
              value={form.content}
              onChange={(e)=>setForm(f=>({...f, content: e.target.value}))}
              className="w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/20"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.pinned} onChange={(e)=>setForm(f=>({...f, pinned: e.target.checked}))} />
              Fijar arriba
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="rounded-xl bg-white border border-slate-200 px-4 py-2 text-slate-700 hover:bg-slate-50">Cancelar</button>
              <button type="submit" disabled={saving} className="rounded-xl bg-slate-900 px-4 py-2 text-white hover:shadow disabled:opacity-50">{saving?'Guardando…':'Guardar'}</button>
            </div>
          </div>
          <div className={`text-sm ${msg ? 'text-rose-600' : 'text-slate-400'}`}>{msg}</div>
        </form>
      </div>
    </div>
  );
}
